export type LegalDocKey = "terms" | "privacy" | "cookies" | "security" | "subprocessors";

export type LegalDocSection = {
  heading: string;
  paragraphs: string[];
  /** Trusted static HTML from this file only (e.g. internal legal links). */
  htmlParagraphs?: string[];
  bullets?: string[];
};

export type LegalDocPayload = {
  title: string;
  effectiveDate: string;
  intro: string;
  sections: LegalDocSection[];
};

const EFFECTIVE = "Effective Date: June 12, 2026";

/** Shared public copy for static landing modals (single source). */
export const LEGAL_DOCUMENTS: Record<LegalDocKey, LegalDocPayload> = {
  cookies: {
    title: "Cookie Policy",
    effectiveDate: EFFECTIVE,
    intro:
      "Email Perfect uses a minimal set of cookies. We do not use analytics cookies, advertising cookies, remarketing technologies, or behavioral profiling cookies.",
    sections: [
      {
        heading: "What we do not use",
        bullets: [
          "Analytics cookies (e.g. Google Analytics)",
          "Advertising or remarketing cookies",
          "Behavioral profiling or cross-site tracking cookies",
        ],
      },
      {
        heading: "Cookies we use",
        paragraphs: ["We set only strictly necessary first-party cookies:"],
        bullets: [
          "ep_session — keeps you signed in (HttpOnly, SameSite=Lax, up to 30 days)",
          "ep_invite_token — holds a pending team invite while you complete sign-in (HttpOnly, up to 24 hours)",
        ],
      },
      {
        heading: "Local storage",
        paragraphs: [
          "The application also uses browser local storage for in-app functionality such as draft work, undo/redo, and sign-in convenience. Local storage is not a cookie. See our Privacy Policy for details.",
        ],
      },
      {
        heading: "Third-party resources",
        paragraphs: [
          "When you choose to use certain features, third-party services may set their own cookies on their domains, including Google Sign-In, optional Canva media integration, and Google Fonts loaded in the editor. Those services are governed by their own policies.",
        ],
      },
      {
        heading: "Cookie banner",
        paragraphs: [
          "Because our first-party cookies are strictly necessary for authentication and invite completion, we do not display a cookie consent banner today.",
        ],
      },
      {
        heading: "Your choices",
        bullets: [
          "Sign out to clear your session cookie",
          "Use browser settings to block or delete cookies (sign-in and invites may not work)",
        ],
      },
    ],
  },
  privacy: {
    title: "Privacy Policy",
    effectiveDate: EFFECTIVE,
    intro:
      "We are committed to protecting your privacy and complying with applicable privacy laws, including GDPR where applicable.",
    sections: [
      {
        heading: "1. Data We Collect",
        paragraphs: ["We collect only what is necessary to operate the service:"],
        bullets: [
          "Account data (display name, email address, optional avatar)",
          "Basic usage data necessary to operate the service",
        ],
      },
      {
        heading: "2. User Content",
        paragraphs: [
          "Email Perfect is intended for the creation of email templates and designs. You should avoid uploading, storing, or processing sensitive personal information within projects unless you have the legal right to do so. You remain responsible for the content you create and upload to the platform.",
          "We do not actively monitor or control all content entered by users and are not responsible for personal data you choose to include in your projects.",
        ],
      },
      {
        heading: "3. Local Storage",
        paragraphs: ["The application uses browser local storage to improve functionality, including:"],
        bullets: [
          "saving work-in-progress",
          "enabling undo/redo actions",
          "maintaining session-related convenience on your device",
          "This data remains on your device and is not transmitted to our servers unless required to provide core functionality.",
        ],
      },
      {
        heading: "4. How We Use Data",
        paragraphs: [
          "We store account, team, project, and related service data necessary to operate Email Perfect.",
          "We use your data to provide and maintain the service, authenticate users, and improve performance and security.",
          "We do not sell your personal data. We do not build advertising profiles, run ad targeting, or track you across other websites for marketing purposes.",
        ],
      },
      {
        heading: "5. Promotional Showcase Permission",
        paragraphs: [
          "With your separate explicit opt-in in Account settings, we may display screenshots, templates, designs, company names, and logos associated with your account for marketing, educational, or promotional purposes.",
          "Consent to promotional showcase usage does not constitute consent to receive marketing emails. Marketing communications, if offered, require separate consent where required by law.",
          "When granted, we record the time of consent and the account identifier associated with that choice.",
        ],
      },
      {
        heading: "6. Payments",
        paragraphs: [
          "If paid plans are offered in the future, payment processing may be provided by third-party payment providers. Email Perfect does not store full payment card information. Additional details will be provided before payment processing is enabled.",
        ],
      },
      {
        heading: "7. Data Storage and Transfers",
        paragraphs: [
          "Our servers are hosted in the United States. By using the service, you acknowledge that your data may be transferred to and stored in the United States.",
          "We take reasonable steps to ensure appropriate safeguards are in place for such transfers.",
        ],
      },
      {
        heading: "8. Subprocessors",
        paragraphs: ["We use third-party service providers to operate Email Perfect."],
        htmlParagraphs: [
          'See our <a data-legal-static href="/subprocessors">Subprocessors</a> page for a list of vendors and their purposes.',
        ],
      },
      {
        heading: "9. Cookies",
        paragraphs: ["See our Security page for details on cookies and similar technologies."],
        htmlParagraphs: ['Read the <a href="/security/cookies">Cookies</a> section on our Security page.'],
      },
      {
        heading: "10. Legal Basis (GDPR)",
        paragraphs: ["We process personal data based on:"],
        bullets: [
          "contract (to provide the service)",
          "legitimate interests (to improve and secure the platform)",
          "consent (where required, such as promotional showcase permission)",
        ],
      },
      {
        heading: "11. Data Retention",
        paragraphs: [
          "We retain personal data only as long as necessary to provide the service or comply with legal obligations.",
        ],
      },
      {
        heading: "12. Your Rights (GDPR)",
        paragraphs: ["You have the right to:"],
        bullets: [
          "access your data",
          "correct inaccurate data",
          "request deletion",
          "object to or restrict processing",
          "request data portability",
        ],
      },
      {
        heading: "13. Data Export",
        paragraphs: [
          "To request a copy of your data, contact support@emailperfect.io. We will provide your account and project data in a commonly used machine-readable format where reasonably possible.",
        ],
      },
      {
        heading: "14. Data Processing Addendum (DPA)",
        paragraphs: [
          "Data Processing Addendum (DPA) documentation is available upon request at privacy@emailperfect.io.",
        ],
      },
      {
        heading: "15. Account Deletion",
        paragraphs: [
          "You may delete your account from Account settings. Deleting your account permanently removes your account information from Email Perfect. Teams and projects you owned may become unreachable if ownership is not transferred before deletion; contact support for assistance.",
        ],
      },
      {
        heading: "16. Availability and Customer Content",
        paragraphs: [
          "Customers retain ownership of their content.",
          "Data export requests are available by contacting support@emailperfect.io.",
        ],
      },
      {
        heading: "17. Security",
        paragraphs: ["We implement reasonable technical and organizational measures to protect your data."],
        htmlParagraphs: ['Learn more on our <a href="/security">Security</a> page.'],
      },
      {
        heading: "18. Contact Information",
        paragraphs: [
          "Questions regarding privacy, GDPR requests, data access requests, or account deletion requests can be sent to: privacy@emailperfect.io",
        ],
      },
      {
        heading: "19. Changes",
        paragraphs: ["We may update this policy. Continued use of the service means you accept the updated policy."],
      },
    ],
  },
  terms: {
    title: "Terms of Service",
    effectiveDate: EFFECTIVE,
    intro: "By using this service, you agree to the following terms.",
    sections: [
      {
        heading: "1. Use of the Service",
        paragraphs: [
          "You may use the service only in compliance with applicable laws. You are responsible for all content you create, upload, or distribute through the platform.",
        ],
      },
      {
        heading: "2. Accounts",
        paragraphs: [
          "You sign in using passwordless authentication (magic link or Google Sign-In). You are responsible for maintaining access to your email account and for all activities under your Email Perfect account.",
          "You may use a display name of your choice, including a pseudonym, provided it does not violate applicable law or these terms.",
        ],
      },
      {
        heading: "3. User Content",
        paragraphs: [
          "Email Perfect is intended for the creation of email templates and designs. You should avoid uploading, storing, or processing sensitive personal information within projects unless you have the legal right to do so. You remain responsible for the content you create and upload to the platform.",
        ],
      },
      {
        heading: "4. Intellectual Property",
        paragraphs: [
          "You retain ownership of your content. By using the service, you grant us a limited license to process and store your content solely to operate and improve the product.",
        ],
      },
      {
        heading: "5. Promotional Showcase Permission",
        paragraphs: [
          "We may use screenshots, templates, designs, company names, and logos associated with your account for marketing, educational, or promotional purposes only where you have granted Showcase Permission in Account settings.",
          "Where personally identifiable information would reasonably appear in such materials, we will anonymize, redact, substitute, or otherwise obscure that information before publication, using commercially reasonable efforts.",
          "This section does not limit our ability to describe or demonstrate the Service using generic or synthetic examples that do not incorporate your specific content.",
        ],
      },
      {
        heading: "6. Payments",
        paragraphs: [
          "If paid plans are offered in the future, payment processing may be provided by third-party payment providers. Email Perfect does not store full payment card information. Additional details will be provided before payment processing is enabled.",
        ],
      },
      {
        heading: "7. Service Availability",
        paragraphs: [
          'The service is provided "as is" without guarantees of uptime, performance, or availability. We may modify or discontinue features at any time.',
        ],
      },
      {
        heading: "8. Limitation of Liability",
        paragraphs: [
          "To the maximum extent permitted by law, we are not liable for indirect, incidental, or consequential damages arising from your use of the service.",
        ],
      },
      {
        heading: "9. Indemnification",
        paragraphs: [
          "You agree to indemnify and hold harmless the company, its owners, affiliates, and employees from any claims, damages, liabilities, and expenses arising from:",
        ],
        bullets: [
          "your use of the service",
          "your violation of these terms",
          "your content, including any personal or sensitive data you choose to include",
        ],
      },
      {
        heading: "10. Termination",
        paragraphs: ["We may suspend or terminate your access at any time if you violate these terms."],
      },
      {
        heading: "11. Governing Law",
        paragraphs: ["These terms are governed by the laws of Canada, with jurisdiction in Nova Scotia."],
      },
      {
        heading: "12. Changes",
        paragraphs: ["We may update these terms from time to time. Continued use of the service means you accept the updated terms."],
      },
    ],
  },
  security: {
    title: "Security",
    effectiveDate: EFFECTIVE,
    intro:
      "Email Perfect is designed with security and privacy in mind. This page summarizes our security practices for customers and vendor review teams.",
    sections: [
      {
        heading: "Our commitments",
        bullets: [
          "We never sell customer data",
          "We never train AI models on customer content",
          "Account deletion and data export available on request",
          "DPA available on request at privacy@emailperfect.io",
          "Support access to customer content limited to support and hypercare",
          "Subprocessor transparency at /subprocessors",
        ],
      },
      {
        heading: "Hosting",
        paragraphs: [
          "Email Perfect is hosted in the United States on Namecheap managed hosting (cPanel). Customer data is stored on production servers separate from source code deploys.",
        ],
      },
      {
        heading: "Encryption",
        bullets: [
          "TLS 1.2 or higher for all API and application traffic",
          "Hosting-provider storage controls for data at rest on production servers",
          "Encrypted off-site backup archives (AES-256)",
        ],
      },
      {
        heading: "Authentication",
        bullets: [
          "Magic-link authentication",
          "Google Sign-In support",
          "No password storage",
          "Administrative access protected via Google Sign-In (multi-factor authentication on the Google account)",
        ],
      },
      {
        heading: "Access Controls",
        bullets: [
          "Role-based access controls",
          "Team-level permissions",
          "Project-level permissions",
        ],
      },
      {
        heading: "Backups",
        paragraphs: [
          "Production data is backed up regularly and stored separately from production infrastructure.",
        ],
      },
      {
        heading: "Data Privacy",
        bullets: [
          "GDPR-ready privacy controls",
          "Account deletion available from Account settings",
          "Data export available upon request",
          "DPA available upon request at privacy@emailperfect.io",
        ],
      },
      {
        heading: "Monitoring",
        paragraphs: [
          "Uptime monitoring and alerting for service availability; operational logging for security investigations.",
        ],
      },
      {
        heading: "Incident Response",
        paragraphs: [
          "Email Perfect maintains a documented incident response process and will notify affected customers of confirmed security incidents where appropriate.",
        ],
      },
      {
        heading: "Availability and Support",
        bullets: [
          "Customers retain ownership of their content",
          "Data export requests: support@emailperfect.io",
        ],
      },
      {
        heading: "Security FAQ",
        bullets: [
          "Do you sell customer data? No.",
          "Do you use customer content to train AI models? No.",
          "Are passwords stored? No. Email Perfect uses passwordless authentication.",
          "Can I delete my data? Yes.",
          "Can I export my data? Yes, upon request.",
          "Do you offer a DPA? Yes, upon request at privacy@emailperfect.io.",
        ],
      },
      {
        heading: "Contact",
        paragraphs: ["security@emailperfect.io · privacy@emailperfect.io"],
      },
    ],
  },
  subprocessors: {
    title: "Subprocessors",
    effectiveDate: EFFECTIVE,
    intro:
      "Email Perfect uses the following third-party service providers to operate the platform. This list reflects providers in use as of the effective date above.",
    sections: [
      {
        heading: "Current subprocessors",
        bullets: [
          "Namecheap — application hosting (United States)",
          "Google — optional authentication (OAuth)",
          "Canva — optional media integration",
          "Resend — transactional email delivery",
        ],
      },
      {
        heading: "Updates",
        paragraphs: [
          "We may update subprocessors as our infrastructure evolves. Material changes will be reflected on this page.",
        ],
      },
      {
        heading: "Questions",
        paragraphs: ["privacy@emailperfect.io"],
      },
    ],
  },
};

/** Paths that open a public document modal. Security and cookies live on /security. */
export const PUBLIC_DOC_PATHS: Record<string, LegalDocKey> = {
  "/terms": "terms",
  "/privacy": "privacy",
  "/subprocessors": "subprocessors",
};
